// ===========================================
// MarquesVehicule — Marques & modèles véhicule (refonte UX)
// ===========================================
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import {
    Table, Card, Input, Button, Grid, Modal, Form, Popconfirm, Space, Tooltip, Tag, Empty, List,
} from 'antd';
import {
    SearchOutlined, PlusOutlined, EditOutlined, DeleteOutlined, CarOutlined,
    EyeOutlined, ArrowLeftOutlined, AppstoreOutlined, DatabaseOutlined,
} from '@ant-design/icons';
import { marquesVehiculeService } from '../../common/services';

import MiniKpiCard from '../clients/components/MiniKpiCard';

import './marques-vehicule.page.scss';

const getInitials = (nom) =>
    (nom || '')
        .split(/\s+/)
        .filter(Boolean)
        .map((w) => w[0])
        .join('')
        .substring(0, 2)
        .toUpperCase();

const MarquesVehicule = () => {
    const [marques, setMarques] = useState([]);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState('');
    const [modalVisible, setModalVisible] = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [modalLoading, setModalLoading] = useState(false);
    const [selectedMarque, setSelectedMarque] = useState(null);
    const [modeleInput, setModeleInput] = useState('');
    const [modeleSearch, setModeleSearch] = useState('');
    const [form] = Form.useForm();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.sm;

    const fetchMarques = async () => {
        setLoading(true);
        try {
            const res = await marquesVehiculeService.getAll();
            const data = Array.isArray(res) ? res : res?.data || [];
            setMarques(data);
        } catch (error) {
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchMarques(); }, []);

    const filteredMarques = useMemo(() => {
        if (!search) return marques;
        const q = search.toLowerCase();
        return marques.filter((m) => (m.nom || '').toLowerCase().includes(q));
    }, [marques, search]);

    const stats = useMemo(() => {
        const totalMarques = marques.length;
        const totalModeles = marques.reduce((s, m) => s + (m.modeles?.length || 0), 0);
        const moyenne = totalMarques > 0 ? Math.round((totalModeles / totalMarques) * 10) / 10 : 0;
        return { totalMarques, totalModeles, moyenne };
    }, [marques]);

    const handleSave = async (values) => {
        try {
            setModalLoading(true);
            if (editingItem) {
                await marquesVehiculeService.update(editingItem._id, values);
                toast.success('Marque mise à jour');
            } else {
                await marquesVehiculeService.create(values);
                toast.success('Marque créée');
            }
            setModalVisible(false);
            setEditingItem(null);
            form.resetFields();
            fetchMarques();
        } catch (error) {
            toast.error(error.message || 'Erreur');
        } finally {
            setModalLoading(false);
        }
    };

    const handleEdit = (record) => {
        setEditingItem(record);
        form.setFieldsValue({ nom: record.nom });
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        try {
            await marquesVehiculeService.delete(id);
            toast.success('Marque supprimée');
            fetchMarques();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleSeed = async () => {
        try {
            await marquesVehiculeService.seed();
            toast.success('Marques initialisées');
            fetchMarques();
        } catch (error) {
            toast.error(error.message || 'Erreur');
        }
    };

    const handleAddModele = async () => {
        const nom = modeleInput.trim();
        if (!nom || !selectedMarque) return;
        try {
            const res = await marquesVehiculeService.addModele(selectedMarque._id, { nom });
            const updated = res?.data || res;
            setSelectedMarque(updated);
            setModeleInput('');
            fetchMarques();
            toast.success('Modèle ajouté');
        } catch (error) {
            toast.error(error.message || 'Erreur');
        }
    };

    const handleRemoveModele = async (modele) => {
        try {
            const res = await marquesVehiculeService.removeModele(selectedMarque._id, modele);
            const updated = res?.data || res;
            setSelectedMarque(updated);
            fetchMarques();
            toast.success('Modèle supprimé');
        } catch (error) {
            toast.error(error.message || 'Erreur');
        }
    };

    const openCreateModal = () => {
        setEditingItem(null);
        form.resetFields();
        setModalVisible(true);
    };

    // ─── Vue détail des modèles d'une marque ────────────────────────────
    if (selectedMarque) {
        const modeles = selectedMarque.modeles || [];
        const filteredModeles = modeleSearch
            ? modeles.filter((m) => m.toLowerCase().includes(modeleSearch.toLowerCase()))
            : modeles;

        return (
            <div className="marques-vehicule-page">
                <div className="marque-detail-layout">
                    {/* Sidebar */}
                    <aside className="marque-detail-sidebar">
                        <Card className="marque-detail-sidebar-card">
                            <button
                                type="button"
                                className="sidebar-back-btn"
                                onClick={() => { setSelectedMarque(null); setModeleSearch(''); setModeleInput(''); }}
                            >
                                <ArrowLeftOutlined /> Retour aux marques
                            </button>

                            <div className="sidebar-identity">
                                <div className="sidebar-marque-logo">
                                    {getInitials(selectedMarque.nom) || <CarOutlined />}
                                </div>
                                <h2 className="sidebar-name">{selectedMarque.nom}</h2>
                                <Tag color="geekblue" className="sidebar-count-tag">
                                    {modeles.length} modèle{modeles.length > 1 ? 's' : ''}
                                </Tag>
                            </div>

                            <div className="sidebar-divider" />

                            <div className="sidebar-info">
                                <p className="sidebar-hint">
                                    Ajoutez ou supprimez les modèles associés à la marque <strong>{selectedMarque.nom}</strong>.
                                </p>
                            </div>
                        </Card>
                    </aside>

                    {/* Main */}
                    <div className="marque-detail-main">
                        <Card className="marque-detail-card">
                            <div className="marque-detail-card-header">
                                <h3 className="marque-detail-card-title">
                                    <CarOutlined /> Modèles de {selectedMarque.nom}
                                </h3>
                            </div>

                            <div className="marque-detail-toolbar">
                                <Input
                                    placeholder="Nouveau modèle…"
                                    value={modeleInput}
                                    onChange={(e) => setModeleInput(e.target.value)}
                                    onPressEnter={handleAddModele}
                                    className="modele-input"
                                />
                                <Button
                                    type="primary"
                                    icon={<PlusOutlined />}
                                    onClick={handleAddModele}
                                    className="modele-btn-add"
                                    disabled={!modeleInput.trim()}
                                >
                                    Ajouter
                                </Button>
                                <div className="marque-detail-toolbar-spacer" />
                                <Input
                                    placeholder="Filtrer les modèles…"
                                    prefix={<SearchOutlined />}
                                    value={modeleSearch}
                                    onChange={(e) => setModeleSearch(e.target.value)}
                                    allowClear
                                    className="modele-search"
                                />
                            </div>

                            <div className="modele-grid">
                                {filteredModeles.length === 0 ? (
                                    <Empty
                                        description={modeleSearch ? 'Aucun modèle trouvé' : 'Aucun modèle. Ajoutez-en un avec le champ ci-dessus.'}
                                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                                    />
                                ) : (
                                    filteredModeles.map((modele) => (
                                        <div key={modele} className="modele-chip">
                                            <CarOutlined className="modele-chip-icon" />
                                            <span className="modele-chip-name">{modele}</span>
                                            <Popconfirm
                                                title="Supprimer ce modèle ?"
                                                onConfirm={() => handleRemoveModele(modele)}
                                                okText="Oui"
                                                cancelText="Non"
                                                okButtonProps={{ danger: true }}
                                            >
                                                <button
                                                    type="button"
                                                    className="modele-chip-remove"
                                                    aria-label={`Supprimer ${modele}`}
                                                >
                                                    <DeleteOutlined />
                                                </button>
                                            </Popconfirm>
                                        </div>
                                    ))
                                )}
                            </div>
                        </Card>
                    </div>
                </div>
            </div>
        );
    }

    // ─── Liste des marques ─────────────────────────────────────────────
    const columns = [
        {
            title: 'Marque',
            dataIndex: 'nom',
            key: 'nom',
            sorter: (a, b) => (a.nom || '').localeCompare(b.nom || ''),
            render: (t) => (
                <div className="marque-cell">
                    <div className="marque-logo">{getInitials(t) || <CarOutlined />}</div>
                    <span className="marque-name">{t || '—'}</span>
                </div>
            ),
        },
        {
            title: 'Modèles',
            dataIndex: 'modeles',
            key: 'modeles',
            width: 200,
            align: 'center',
            sorter: (a, b) => (a.modeles?.length || 0) - (b.modeles?.length || 0),
            render: (m) => (
                <Tag icon={<AppstoreOutlined />} color="geekblue" className="marque-count-tag">
                    {(m || []).length} modèle{(m || []).length > 1 ? 's' : ''}
                </Tag>
            ),
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 160,
            align: 'center',
            fixed: isMobile ? false : 'right',
            render: (_, record) => (
                <Space size={6}>
                    <Tooltip title="Voir les modèles">
                        <Button
                            size="small"
                            icon={<EyeOutlined />}
                            className="marque-action-btn marque-action-view"
                            aria-label="Voir les modèles"
                            onClick={() => { setSelectedMarque(record); setModeleSearch(''); }}
                        />
                    </Tooltip>
                    <Tooltip title="Modifier">
                        <Button
                            size="small"
                            icon={<EditOutlined />}
                            className="marque-action-btn marque-action-edit"
                            aria-label="Modifier la marque"
                            onClick={() => handleEdit(record)}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Supprimer cette marque ?"
                        description="Tous les modèles associés seront perdus."
                        okText="Oui"
                        cancelText="Non"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => handleDelete(record._id)}
                    >
                        <Tooltip title="Supprimer">
                            <Button
                                size="small"
                                icon={<DeleteOutlined />}
                                className="marque-action-btn marque-action-delete"
                                aria-label="Supprimer la marque"
                            />
                        </Tooltip>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div className="marques-vehicule-page">
            {/* Header */}
            <header className="marques-header">
                <div className="marques-header-text">
                    <h1 className="marques-title">
                        <CarOutlined /> Marques & modèles véhicule
                    </h1>
                    <p className="marques-subtitle">Référentiel des marques et modèles utilisés sur les contrats</p>
                </div>
                <div className="marques-header-actions">
                    {marques.length === 0 && (
                        <Popconfirm
                            title="Initialiser avec les marques par défaut ?"
                            okText="Oui"
                            cancelText="Non"
                            onConfirm={handleSeed}
                        >
                            <Button icon={<DatabaseOutlined />} className="marques-btn-seed">
                                Initialiser
                            </Button>
                        </Popconfirm>
                    )}
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={openCreateModal}
                        className="marques-btn-add"
                    >
                        {!isMobile && 'Nouvelle marque'}
                    </Button>
                </div>
            </header>

            {/* KPIs */}
            <section className="marques-kpis" aria-label="Indicateurs marques véhicule">
                <MiniKpiCard
                    icon={<CarOutlined />}
                    label="Total des marques"
                    value={stats.totalMarques}
                    tone="blue"
                />
                <MiniKpiCard
                    icon={<AppstoreOutlined />}
                    label="Total des modèles"
                    value={stats.totalModeles}
                    tone="indigo"
                />
                <MiniKpiCard
                    icon={<DatabaseOutlined />}
                    label="Modèles / marque"
                    value={stats.moyenne}
                    tone="green"
                />
            </section>

            {/* Carte principale */}
            <Card className="marques-main-card">
                <div className="marques-toolbar">
                    <Input
                        placeholder="Rechercher une marque…"
                        prefix={<SearchOutlined />}
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        allowClear
                        className="marques-search"
                    />
                </div>

                <Table
                    dataSource={filteredMarques}
                    columns={columns}
                    rowKey="_id"
                    loading={{ spinning: loading, size: 'large', tip: 'Chargement…' }}
                    size="middle"
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: loading ? <div style={{ minHeight: 220 }} /> : <Empty description="Aucune marque" image={Empty.PRESENTED_IMAGE_SIMPLE} />,
                    }}
                    pagination={{
                        pageSize: 15,
                        showTotal: (t) => `${t} marque${t > 1 ? 's' : ''}`,
                        showSizeChanger: true,
                    }}
                    onRow={(record) => ({
                        style: { cursor: 'pointer' },
                        onClick: (e) => {
                            // ignore action buttons clicks
                            if (e.target.closest('.marque-action-btn') || e.target.closest('.ant-popover')) return;
                            setSelectedMarque(record);
                            setModeleSearch('');
                        },
                    })}
                />
            </Card>

            {/* Modal créer / modifier */}
            <Modal
                title={
                    <span className="marques-modal-title">
                        <CarOutlined /> {editingItem ? 'Modifier la marque' : 'Nouvelle marque'}
                    </span>
                }
                open={modalVisible}
                onCancel={() => setModalVisible(false)}
                footer={null}
                destroyOnClose
                width={460}
                className="marques-modal"
            >
                <Form form={form} layout="vertical" onFinish={handleSave} className="marques-form">
                    <Form.Item
                        name="nom"
                        label="Nom de la marque"
                        rules={[{ required: true, message: 'Le nom de la marque est obligatoire' }]}
                        normalize={(v) => v?.toUpperCase()}
                    >
                        <Input
                            autoFocus
                            prefix={<CarOutlined />}
                            placeholder="Ex : TOYOTA"
                        />
                    </Form.Item>
                    <div className="marques-form-actions">
                        <Button onClick={() => setModalVisible(false)} className="marques-btn-cancel">
                            Annuler
                        </Button>
                        <Button
                            type="primary"
                            htmlType="submit"
                            loading={modalLoading}
                            className="marques-btn-submit"
                        >
                            {editingItem ? 'Enregistrer' : 'Créer la marque'}
                        </Button>
                    </div>
                </Form>
            </Modal>
        </div>
    );
};

export default MarquesVehicule;
